import React, { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import Grid2 from '@mui/material/Unstable_Grid2/Grid2';
import {
    Button,
    CircularProgress,
    Divider,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Typography,
    styled,
    Box,
    Chip,
} from '@mui/material';
import { SideMenuPageContainer } from '../../components/side_menu_page_container/SideMenuPageContainer';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import {
    addPdfFileAsync,
    compressPdfAsync,
    downloadCompressedPdfAsync,
    CompressionQuality,
    selectPdfCompressionState,
    setCompressionQuality,
    resetState,
} from './PdfCompressionSlice';
import { useTranslation } from 'react-i18next';
import { HomeCard } from '../../components/home_card/HomeCard';
import CompressionImage from "../../assets/Compression.webp";
import PdfMergerImage from "../../assets/PdfMerger.webp";
import AdSense from '../../components/adsense/AdSense';
import { ADSENSE_SLOTS } from '../../config/adsense';

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

export function PdfCompression() {
    const dispatch = useAppDispatch();
    const state = useAppSelector(selectPdfCompressionState);
    const { t } = useTranslation();

    const handleFileUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files && files[0]) {
            dispatch(addPdfFileAsync(files[0]));
        }
    }, [dispatch]);

    const handleCompress = useCallback(() => {
        dispatch(compressPdfAsync());
    }, [dispatch]);

    const handleQualityChange = useCallback((quality: CompressionQuality) => {
        dispatch(setCompressionQuality(quality));
    }, [dispatch]);

    const handleReset = useCallback(() => {
        dispatch(resetState());
    }, [dispatch]);

    const handleDownload = useCallback(() => {
        dispatch(downloadCompressedPdfAsync());
    }, [dispatch]);

    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    return (
        <SideMenuPageContainer
            pageTitle={t('pdfCompression.title')}
            pageSeoTitle={t('pdfCompression.seoTitle')}
            pageDescription={t('pdfCompression.description')}
        >
            <Box sx={{ width: { xs: 300, sm: 350, md: 600, lg: 700 } }}>
                <Grid2
                    container
                    spacing={2}
                    sx={{ mt: 2 }}
                    columns={{ xs: 6, sm: 6, md: 12, lg: 12 }}
                >
                    <Grid2 xs={12}>
                        <Typography variant="h2" sx={{ mb: 2 }}>
                            {t('pdfCompression.mainHeading')}
                        </Typography>
                    </Grid2>

                    <Grid2 xs={12}>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                            {t('pdfCompression.step1')}
                        </Typography>
                    </Grid2>

                    <Grid2 xs={12}>
                        <Stack direction="column" justifyContent="center" alignItems="center">
                            <Button
                                component="label"
                                variant="contained"
                                startIcon={<CloudUploadIcon sx={{ color: '#FFD9A8' }} />}
                                disabled={state.compressionInProgress}
                            >
                                <Typography>{t('pdfCompression.selectButton')}</Typography>
                                <VisuallyHiddenInput 
                                    type="file" 
                                    accept=".pdf" 
                                    onChange={handleFileUpload}
                                />
                            </Button>
                        </Stack>
                    </Grid2>

                    {state.pdfFile && (
                        <>
                            <Grid2 xs={12}>
                                <Paper sx={{ p: 2, mt: 2 }}>
                                    <Typography variant="h4" sx={{ mb: 1 }}>
                                        {t('pdfCompression.selectedFile')}
                                    </Typography>
                                    <Typography variant="body1" sx={{ mb: 1 }}>
                                        {state.pdfFile.name}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#aedff2' }}>
                                        {t('pdfCompression.originalSize')}: {formatFileSize(state.pdfFile.originalSize)}
                                    </Typography>
                                    {state.pdfFile.compressedSize && (
                                        <>
                                            <Typography variant="body2" sx={{ color: '#aedff2' }}>
                                                {t('pdfCompression.compressedSize')}: {formatFileSize(state.pdfFile.compressedSize)}
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: '#97e286' }}>
                                                {t('pdfCompression.compressionRatio')}: {state.pdfFile.compressionRatio?.toFixed(1)}%
                                            </Typography>
                                        </>
                                    )}
                                </Paper>
                            </Grid2>

                            <Grid2 xs={12}>
                                <Typography variant="body1" sx={{ mb: 1 }}>
                                    {t('pdfCompression.step2')}
                                </Typography>
                            </Grid2>

                            <Grid2 xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>{t('pdfCompression.qualityLabel')}</InputLabel>
                                    <Select
                                        value={state.compressionQuality}
                                        label={t('pdfCompression.qualityLabel')}
                                        onChange={(e) => handleQualityChange(e.target.value as CompressionQuality)}
                                        disabled={state.compressionInProgress}
                                    >
                                        <MenuItem value={CompressionQuality.low}>
                                            {t('pdfCompression.qualityLow')}
                                        </MenuItem>
                                        <MenuItem value={CompressionQuality.medium}>
                                            {t('pdfCompression.qualityMedium')}
                                        </MenuItem>
                                        <MenuItem value={CompressionQuality.high}>
                                            {t('pdfCompression.qualityHigh')}
                                        </MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid2>

                            <Grid2 xs={12}>
                                <Stack direction="row" spacing={2} justifyContent="center">
                                    <Button
                                        variant="contained"
                                        onClick={handleCompress}
                                        disabled={state.compressionInProgress}
                                        startIcon={
                                            state.compressionInProgress ? (
                                                <CircularProgress size={20} sx={{ color: '#FFD9A8' }} />
                                            ) : undefined
                                        }
                                    >
                                        {state.compressionInProgress
                                            ? t('pdfCompression.compressing')
                                            : t('pdfCompression.compressButton')}
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        onClick={handleReset}
                                        disabled={state.compressionInProgress}
                                        startIcon={<DeleteIcon />}
                                    >
                                        {t('pdfCompression.resetButton')}
                                    </Button>
                                </Stack>
                            </Grid2>

                            {state.pdfFile.compressedSize && (
                                <Grid2 xs={12}>
                                    <Stack direction="row" spacing={2} justifyContent="center">
                                        <Button
                                            variant="contained"
                                            onClick={handleDownload}
                                            startIcon={<DownloadIcon sx={{ color: '#FFD9A8' }} />}
                                        >
                                            {t('pdfCompression.downloadButton')}
                                        </Button>
                                    </Stack>
                                </Grid2>
                            )}
                        </>
                    )}

                    {state.error && (
                        <Grid2 xs={12}>
                            <Typography variant="error" sx={{ mt: 2 }}>
                                {state.error}
                            </Typography>
                        </Grid2>
                    )}

                    <Grid2 xs={12}>
                        <Divider sx={{ my: 3 }} />
                    </Grid2>

                    <Grid2 xs={12}>
                        <AdSense
                            adSlot={ADSENSE_SLOTS.IN_ARTICLE}
                            adFormat="fluid"
                            adLayoutKey="-fb+5w+4e-db+86"
                            sx={{ mb: 3 }}
                            />
                        <Typography variant="h3" sx={{ mb: 2 }}>
                            {t('pdfCompression.content.freeHeading')}
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                            {t('pdfCompression.content.freeDescription')}
                        </Typography>
                    </Grid2>

                    <Grid2 xs={12}>
                        <Typography variant="h4" sx={{ mb: 1 }}>
                            {t('pdfCompression.content.howToHeading')}
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                            {t('pdfCompression.content.howToDescription')}
                        </Typography>
                    </Grid2>

                    <Grid2 xs={12}>
                        <Typography variant="h4" sx={{ mb: 1 }}>
                            {t('pdfCompression.content.featuresHeading')}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                            <Chip label={t('pdfCompression.content.feature1')} />
                            <Chip label={t('pdfCompression.content.feature2')} />
                            <Chip label={t('pdfCompression.content.feature3')} />
                            <Chip label={t('pdfCompression.content.feature4')} />
                        </Box>
                    </Grid2>

                    <Grid2 xs={12}>
            <Typography variant="h3" sx={{ mt: 3, mb: 1 }}>
              {t("qrCodeGenerator.related")}
            </Typography>
          </Grid2>

          <Grid2 xs={6}>
            <HomeCard
              languageResource="compression"
              link="/Compression"
              smallCard={true}
              image={CompressionImage}
            />
          </Grid2>
          <Grid2 xs={6}>
            <HomeCard
              languageResource="pdfMerger"
              link="/PdfMerger"
              smallCard={true}
              image={PdfMergerImage}
            />
          </Grid2>
                </Grid2>
            </Box>
        </SideMenuPageContainer>
    );
}
